/**
 * spk-id.ts — device-unique, persistent, monotonic signed-pre-key id allocator.
 *
 * Every device hardcoded signedPreKey.keyId = 1 historically (crypto/keys.ts), so
 * keyId could not distinguish a device's SPK rotations or sibling devices' SPKs —
 * the root of the 2026-06-11 spkPubMatch=false cascade loop. This allocator hands
 * out ids in a unique band [SPK_ID_MIN_UNIQUE, SPK_ID_MAX), seeded once per device
 * at a random base so collisions across devices are astronomically unlikely and
 * telemetry is unambiguous.
 */
const STORE_KEY = "kp_spk_next_id";

/** Lower bound of the unique band. Ids below this are legacy (the old `1`). */
export const SPK_ID_MIN_UNIQUE = 1000;
/** Upper bound — stays under FALLBACK_PREKEY_ID (2_000_000_000) so SPK ids never
 *  collide with the fallback id and round-trip through int4-range jsonb. */
export const SPK_ID_MAX = 1_900_000_000;

/** Random seed inside the lower half of the band so a device has ~1e9 ids of
 *  monotonic headroom before it ever needs to wrap. */
function seedBase(): number {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return SPK_ID_MIN_UNIQUE + (buf[0]! % (SPK_ID_MAX / 2 - SPK_ID_MIN_UNIQUE));
}

function readNext(): number {
  const raw = localStorage.getItem(STORE_KEY);
  const n = raw === null ? NaN : parseInt(raw, 10);
  if (Number.isNaN(n) || n < SPK_ID_MIN_UNIQUE || n >= SPK_ID_MAX) {
    const seeded = seedBase();
    localStorage.setItem(STORE_KEY, String(seeded));
    return seeded;
  }
  return n;
}

/** The id that the next `nextSpkId()` call will return (without consuming it). */
export function peekNextSpkId(): number {
  return readNext();
}

/** Allocate the next unique SPK id and advance the persistent counter. */
export function nextSpkId(): number {
  const id = readNext();
  const advanced = id + 1 >= SPK_ID_MAX ? seedBase() : id + 1;
  localStorage.setItem(STORE_KEY, String(advanced));
  return id;
}
